import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";

interface Coupon {
  id: string;
  code: string;
  title: string;
  description: string;
  discountPercent: number | null;
  expiresAt: string | null;
  usedAt: string | null;
  assignedAt: string;
}

interface Props { userId: string; }

export default function MyCouponsSection({ userId }: Props) {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCoupons = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("user_coupons")
        .select("id, assigned_at, used_at, coupons(id, code, title, description, discount_percent, expires_at)")
        .eq("user_id", userId)
        .order("assigned_at", { ascending: false });

      if (data) {
        setCoupons(data.map((r) => {
          const c = r.coupons as Record<string, unknown>;
          return {
            id: r.id as string,
            code: c.code as string,
            title: c.title as string,
            description: c.description as string,
            discountPercent: c.discount_percent as number | null,
            expiresAt: c.expires_at as string | null,
            usedAt: r.used_at as string | null,
            assignedAt: r.assigned_at as string,
          };
        }));
      }
      setLoading(false);
    };
    fetchCoupons();
  }, [userId]);

  if (loading) return <div className="py-12 text-center text-stone-400 text-sm">Yükleniyor...</div>;

  if (coupons.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-stone-200 rounded-2xl">
        <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
          <i className="ri-coupon-line text-amber-500 text-2xl" />
        </div>
        <p className="text-stone-700 font-semibold mb-1">Henüz kuponunuz yok</p>
        <p className="text-stone-400 text-sm">Admin tarafından tanımlanan kuponlar burada görünür.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-bold text-stone-900 text-2xl">Kuponlarım</h2>
          <p className="text-stone-500 text-sm mt-1">Size özel indirim ve fırsatlar.</p>
        </div>
        <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full">{coupons.length} kupon</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {coupons.map((c) => {
          const expired = c.expiresAt ? new Date(c.expiresAt) < new Date() : false;
          const used = !!c.usedAt;
          return (
            <div key={c.id} className={`relative rounded-2xl border-2 border-dashed p-5 overflow-hidden ${used || expired ? "border-stone-200 opacity-60" : "border-amber-300 bg-amber-50"}`}>
              {(used || expired) && (
                <div className="absolute top-3 right-3 bg-stone-200 text-stone-500 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {used ? "Kullanıldı" : "Süresi Doldu"}
                </div>
              )}
              <div className="flex items-start justify-between mb-2">
                <span className="font-mono font-bold text-amber-700 text-lg tracking-wider">{c.code}</span>
                {c.discountPercent && !used && !expired && (
                  <span className="bg-amber-400 text-white text-sm font-bold px-2.5 py-1 rounded-full">%{c.discountPercent} İNDİRİM</span>
                )}
              </div>
              <p className="font-semibold text-stone-800 mb-1">{c.title}</p>
              {c.description && <p className="text-sm text-stone-500 mb-2">{c.description}</p>}
              <div className="flex items-center gap-3 mt-3">
                {c.expiresAt && (
                  <span className="text-xs text-stone-400">
                    <i className="ri-time-line mr-1" />
                    Son: {new Date(c.expiresAt).toLocaleDateString("tr-TR")}
                  </span>
                )}
                <span className="text-xs text-stone-400">
                  <i className="ri-calendar-line mr-1" />
                  {new Date(c.assignedAt).toLocaleDateString("tr-TR")}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
